import {createSelector} from 'reselect';

import appConfig from '@/config/appConfig.typed';
import {DEVICE_TYPES} from '@/consts/device/deviceTypes';
import {ICC_PRODUCT_STATUS_TYPES} from '@/consts/icc/iccProductStatusTypes';
import * as tncTypes from '@/consts/tnc/tncTypes';
import {TERMS_OF_USE} from '@/consts/tnc/tncTypes';
import productHelpers from '@/services/product/productHelpers';
import tncService from '@/services/tncService';

const selectConsumer = (state) => state.consumer;

const selectConsumerProductsFetchDate = createSelector(selectConsumer, (state) => state.productsFetchDate);
const selectIsConsumerProductsFetchFailed = createSelector(selectConsumer, (state) => state.productsFailed);
const selectConsumerProducts = createSelector(selectConsumer, (state) => state.products);

const selectConsumerData = createSelector(selectConsumer, (state) => state.data);
const selectConsumerFetchDate = createSelector(selectConsumer, (state) => state.consumerFetchDate);

const selectConsumerId = createSelector(selectConsumerData, (data) => data?.consumerId);

const selectConsumerLanguageCode = createSelector(selectConsumerData, (data) => data?.defaultLocaleCode);

const selectConsumerProductsInfo = createSelector(selectConsumer, (state) => {
    return state.productsInfo;
});

const selectConsumerProductsMergedInfo = createSelector(
    selectConsumerProducts,
    selectConsumerProductsInfo,
    (products, productsInfo) => {
        if (!products) return;
        if (products?.length === 0) return [];

        const registeredProducts = products.filter(
            (product) => !product.isHolder && product.status === ICC_PRODUCT_STATUS_TYPES.REGISTERED
        );

        const consumerProductMergedInfo = registeredProducts.map((product) => {
            return productHelpers.mapDeviceData(product, products, productsInfo);
        });

        return consumerProductMergedInfo.filter((product) => product && product.type !== DEVICE_TYPES.DEV_TYPE_UNKNOWN);
    }
);

const makeSelectIsProductRegistered = (codentify) =>
    createSelector(selectConsumerProducts, (products) => {
        const product = products?.find((x) => x.codentify === codentify);

        return product?.status === ICC_PRODUCT_STATUS_TYPES.REGISTERED;
    });

const selectIsConsumerProductsFetched = createSelector(
    selectIsConsumerProductsFetchFailed,
    selectConsumerProductsMergedInfo,
    (isFailed, products) => {
        //failed request considered as fetched
        return isFailed || products !== undefined;
    }
);

const selectIsAnyConsumerProducts = createSelector(selectConsumerProductsMergedInfo, (products) => {
    return products?.length > 0;
});

const selectIsConsumerDataFetched = createSelector(selectConsumerId, (consumerId) => !!consumerId);

const selectConsumerName = createSelector(selectConsumerData, (data) => {
    const {firstName, lastName} = data || {};
    const fullName = [firstName, lastName].filter((str) => str?.length).join(' ');

    return fullName;
});

const selectConsumerFirstName = createSelector(selectConsumerData, (data) => {
    const {firstName} = data || {};

    return firstName;
});

const selectConsumerEmail = createSelector(selectConsumerData, (data) => data?.email);

const selectConsumerMobile = createSelector(selectConsumerData, (data) => data?.mobile);

const selectIsDeliverySkipped = createSelector(selectConsumer, (state) => state.isDeliverySkipped);

const makeSelectConsumerAggregatedAttribute = (type) =>
    createSelector(selectConsumerData, (data) => {
        const {consumerAggregatedAttributes} = data || {};

        if (!consumerAggregatedAttributes) return null;

        const value = consumerAggregatedAttributes.find(
            (x) => x.attributeCode?.toLowerCase() === type.toLowerCase()
        )?.value;

        if (typeof value === 'string') {
            return value;
        }

        return null;
    });

/* TNC */
const selectConsumerTnc = createSelector(selectConsumer, (state) => state.tnc);

const selectConsumerAcceptedTnc = createSelector(selectConsumer, (state) => state.acceptedTnc);

const makeSelectTncByType = (type) =>
    createSelector(selectConsumerTnc, (tnc) => (tnc ? tnc.find((item) => item.screenId === type) : {}));

const makeSelectAcceptedTncByType = (type) =>
    createSelector(selectConsumerAcceptedTnc, (acceptedTnc) =>
        acceptedTnc ? acceptedTnc.find((item) => item.screenId === type) : {}
    );

const selectTncLegals = createSelector(selectConsumerTnc, selectConsumerAcceptedTnc, (tnc, acceptedTnc) => {
    const legals = filterLegals(tnc);
    const acceptedLegals = filterLegals(acceptedTnc);

    return [...legals, ...acceptedLegals];
});

const selectTermsOfUseLegalCode = createSelector(
    makeSelectTncByType(TERMS_OF_USE),
    makeSelectAcceptedTncByType(TERMS_OF_USE),
    (termsOfUse, acceptedTermsOfUse) => {
        const legals = acceptedTermsOfUse?.legals?.length ? acceptedTermsOfUse.legals : termsOfUse?.legals || [];
        const position = appConfig.getIccTermsOfUseLegalPosition() || 0;

        return legals.find((x) => x.position === position)?.code;
    }
);

const makeSelectTncLegalDataByCode = (code) =>
    createSelector(selectTncLegals, (legals) => {
        const legalData = tncService.getTncLegalByTermsCode(legals, code);

        return legalData;
    });

const makeSelectTncLegalsByType = (type) =>
    createSelector(makeSelectTncByType(type), (tnc) => {
        return tnc?.legals || [];
    });

const filterLegals = (tnc) => {
    return tnc?.length
        ? tnc
              .map(({legals}) => {
                  return legals ? [...legals] : [];
              })
              .reduce((prev, next) => [...prev, ...next])
        : [];
};

const selectConsumerProductsToRegUnreg = createSelector(selectConsumer, (state) => state.productsToRegUnreg);

const makeSelectConsumerProductToRegUnreg = (codentify) =>
    createSelector(selectConsumerProductsToRegUnreg, (products) => products?.find((x) => x.codentify === codentify));

const makeSelectUrlByType = (tncType, configPosition) =>
    createSelector(selectConsumerTnc, (tnc) => {
        if (!tnc?.length) return;

        const legals = tnc.find((item) => item.screenId === tncType)?.legals;

        if (!legals?.length) return;

        const position = configPosition() || 0;

        return legals.find((x) => x.position === position)?.webLink;
    });

const selectAttributionNoticeUrl = makeSelectUrlByType(tncTypes.TERMS_OF_USE, () =>
    appConfig.getIccAttributionNoticeLegalPosition()
);

const selectPrivacyNoticeUrl = makeSelectUrlByType(tncTypes.PRIVACY_NOTICE, () =>
    appConfig.getIccPrivacyNoticeLegalPosition()
);
/* TNC */

const makeSelectExternalLink = (key, locale) =>
    createSelector(selectConsumer, (data) => {
        const {externalLinks = []} = data || {};

        return (
            externalLinks.find(
                (x) => x.key?.toLowerCase() === key?.toLowerCase() && x.locale?.toLowerCase() === locale?.toLowerCase()
            ) || {}
        );
    });

export {
    makeSelectAcceptedTncByType,
    makeSelectConsumerAggregatedAttribute,
    makeSelectConsumerProductToRegUnreg,
    makeSelectExternalLink,
    makeSelectIsProductRegistered,
    makeSelectTncByType,
    makeSelectTncLegalDataByCode,
    makeSelectTncLegalsByType,
    selectAttributionNoticeUrl,
    selectConsumer,
    selectConsumerAcceptedTnc,
    selectConsumerData,
    selectConsumerEmail,
    selectConsumerFetchDate,
    selectConsumerFirstName,
    selectConsumerId,
    selectConsumerLanguageCode,
    selectConsumerMobile,
    selectConsumerName,
    selectConsumerProducts,
    selectConsumerProductsFetchDate,
    selectConsumerProductsInfo,
    selectConsumerProductsMergedInfo,
    selectConsumerProductsToRegUnreg,
    selectConsumerTnc,
    selectIsAnyConsumerProducts,
    selectIsConsumerDataFetched,
    selectIsConsumerProductsFetched,
    selectIsDeliverySkipped,
    selectPrivacyNoticeUrl,
    selectTermsOfUseLegalCode,
    selectTncLegals,
};
